import { useState, useMemo, useCallback, useEffect, Dispatch, SetStateAction, memo } from "react";
import { List, ListContent, ListHeader, ListIcon, ListItem } from "semantic-ui-react";

// Serializers
import PublishedDiagRuleNodeSerializer from "@/issara-sdk/types/PublishedDiagRuleNodeSerializer_apps_PRX";

// Type
import { TreeNode, FlatNode, ParsedNode, RunSequence, ALL_BROADCAST_STATUSES } from "./MeFormType";

// Components
import MeFormTreeChild from "./MeFormTreeChild";

// Utils
import { updateContent, buildTree, generateGroupName } from "./MeFormUtils";
import ModMeForm from "./ModMeForm";

// Type
type CardMeFormLeafNodeProps = {
  runSequence: RunSequence;
  // data
  isPublished?: boolean;
  content?: string; // JSON string
  pdrNodeList: PublishedDiagRuleNodeSerializer[];
  selectedNode: TreeNode | null;
  // func
  onSelectNode: (node: TreeNode | null) => void;
  onChangeContent?: (updatedContent: string) => void;
};

// * ----- NOTE ----- * //
/*
[Based on DiagRule]
- label       -> เป็น Question Description
- text        -> Question เป็นชื่อคำถาม / Answer เป็น Suggestion
- condition   -> เป็นคำตอบโดยตรง

* -- New -- *
- group_name  -> auto generated มาจาก Node (Pattern: root.text - node.condition - node.condition - ...)
*/

// Branch Node Component
const RenderBranchList = ({ branchNodes }: { branchNodes: FlatNode[] }) => {
  if (!branchNodes || branchNodes.length === 0) return null;

  const minLevel = Math.min(...branchNodes.map((n) => n.level));
  const indetStep = 8;

  return (
    <List divided relaxed>
      {branchNodes.map((node) => {
        const indent = (node.level - minLevel) * indetStep;

        return (
          <ListItem key={node.id} style={{ paddingLeft: indent }}>
            <ListIcon name="angle right" style={{ marginLeft: 0, marginRight: 5 }} />
            <ListContent>
              <ListHeader>{node.group_name || "(unknown group name)"}</ListHeader>
            </ListContent>
          </ListItem>
        );
      })}
    </List>
  );
};

const CardMeFormLeafNode = (props: CardMeFormLeafNodeProps) => {
  const editor = useDecisionTreeEditor({
    content: props.content || "",
    isPublished: props.isPublished,
    pdrNodeList: props.pdrNodeList,
    runSequence: props.runSequence,
    onSelectNode: props.onSelectNode,
    onChangeContent: props.onChangeContent
  });

  const preview = editor.removeBranchPreview;

  return (
    <div
      style={{
        width: "100%",
        position: "relative",
        overflowX: "auto",
        overflowY: "visible",
        padding: "10px",
        // height: "100%",
        transition: "width 0.2s",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <List divided relaxed style={{ width: "100%" }}>
        {editor.treeData.map((root, index) => (
          <MeFormTreeChild
            isPublished={props.isPublished}
            key={root.id}
            node={root}
            selectedId={props.selectedNode?.id}
            pdrNodeList={props.pdrNodeList}
            hideMap={editor.hideMap}
            isLast={index === root.children!.length - 1}
            onToggle={editor.handlers.onToggle}
            onSelect={editor.handlers.onSelect}
            onAddChild={editor.handlers.onAddChild}
            onAddQuestion={editor.handlers.onAddQuestion}
            onEdit={editor.handlers.onEdit}
            onRemove={editor.handlers.onRemoveChild}
            onRemoveBranch={editor.handlers.onRemoveBranch}
          />
        ))}
      </List>

      {/* --- Confirm Remove Branch Mod --- */}
      <ModMeForm
        open={!!preview}
        onClose={editor.handlers.cancelRemoveBranch}
        onConfirm={editor.handlers.confirmRemoveBranch}
        header="Confirm to remove this branch"
        content={
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              textAlign: "center",
              lineHeight: 2,
              padding: "10px 5px",
            }}
          >
            <label>Would you like to confirm removing this branch?</label>

            <div
              style={{
                maxHeight: "300px",
                overflowY: "auto",
                marginTop: 10,
                textAlign: "left",
              }}
            >
              {/* <RenderBranchList branchNodes={branchNodes} /> */}
              <RenderBranchList branchNodes={preview?.branchNodes || []} />
            </div>
          </div>
        }
      />
    </div>
  );
};

/*================================ USE DECISION TREE EDITOR ==============================*/

type UseDecisionTreeEditorParams = {
  // data
  content: string;
  isPublished?: boolean;
  pdrNodeList: PublishedDiagRuleNodeSerializer[];
  // infra
  runSequence: RunSequence;
  // action
  onSelectNode?: (node: TreeNode | null) => void;
  onChangeContent?: (content: string) => void;
};

type RemoveBranchPreview = {
  nodeId: number;
  branchNodes: FlatNode[];
} | null;

export const useDecisionTreeEditor = ({
  content,
  isPublished,
  pdrNodeList,
  runSequence,
  onSelectNode,
  onChangeContent
}: UseDecisionTreeEditorParams) => {
  const [hideMap, setHideMap] = useState<Record<number, boolean>>({});
  const [removeBranchPreview, setRemoveBranchPreview] = useState<RemoveBranchPreview>(null);

  /*------------------ DERIVED -----------------*/
  const treeData = useMemo(() => buildTree(content), [content]);

  /*------------------ HELPERS -----------------*/
  const updateContentSafe = useCallback(
    (fn: (prev: string) => string) => {
      const next = fn(content);
      onChangeContent?.(next);
    },
    [content, onChangeContent]
  );

  const collectBranchNodes = useCallback(
    (nodeId: number, parsed: ParsedNode): FlatNode[] => {
      const result: FlatNode[] = [];

      const dfs = (id: number) => {
        if (parsed[id]) result.push(parsed[id]);
        Object.values(parsed).forEach((n) => {
          if (n.parent_id === id) dfs(n.id);
        });
      };

      dfs(nodeId);

      return result;
    },
    []
  );

  /*------------------ HANDLERS -----------------*/
  const onToggle = useCallback((id: number) => {
    setHideMap((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  const onSelect = useCallback(
    (node: TreeNode) => {
      if (node.type === "end" && isPublished) {
        const publishedNodeId =
          pdrNodeList.find((pdr) => pdr.node_id === node.id)?.id ?? 0;

        runSequence({
          sequence: "MeForm",
          action: "GET_BROADCAST_NODE_LIST",
          broadcastNodeParams: {
            published_diag_rule_node: publishedNodeId,
            statuses: ALL_BROADCAST_STATUSES,
          },
        });
      }

      onSelectNode?.(node);
    },
    [isPublished, pdrNodeList, runSequence, onSelectNode]
  );

  const onAddChild = useCallback(
    (parentId: number) => {
      updateContentSafe((prev) =>
        updateContent(prev, (parsed) => {
          const newId = Math.max(0, ...Object.keys(parsed).map(Number)) + 1;
          const parentNode = parsed[parentId];
          const level = parentNode ? parentNode.level + 1 : 0;

          parsed[newId] = {
            id: newId,
            parent_id: parentId,
            label: "",
            group_name: "",
            condition: "",
            text: "",
            type: "end",
            level,
            span: 0,
          } as any;

          parsed[newId].group_name = generateGroupName(newId, parsed);
        })
      );
    },
    [updateContentSafe]
  );

  const onAddQuestion = useCallback(
    (nodeId: number) => {
      updateContentSafe((prev) =>
        updateContent(prev, (parsed) => {
          const node = parsed[nodeId];
          if (!node) return;

          parsed[nodeId] = {
            ...node,
            type: "choices",
          };

          const newId = Math.max(0, ...Object.keys(parsed).map(Number)) + 1;

          parsed[newId] = {
            id: newId,
            parent_id: nodeId,
            label: "",
            group_name: "",
            condition: "",
            text: "",
            type: "end",
            level: node.level + 1,
            span: 0,
          } as any;

          parsed[newId].group_name = generateGroupName(newId, parsed);
        })
      );
    },
    [updateContentSafe]
  );

  const onEdit = useCallback(
    (node: TreeNode) => {
      updateContentSafe((prev) =>
        updateContent(prev, (parsed) => {
          if (!parsed[node.id]) return;

          parsed[node.id] = {
            ...parsed[node.id],
            text: node.text,
            condition: node.condition,
          };

          const updateGroup = (id: number) => {
            parsed[id].group_name = generateGroupName(id, parsed);
            Object.values(parsed).forEach((c) => {
              if (c.parent_id === id) updateGroup(c.id);
            });
          };

          updateGroup(node.id);
        })
      );
    },
    [updateContentSafe]
  );

  const onRemoveChild = useCallback(
    (nodeId: number) => {
      updateContentSafe((prev) =>
        updateContent(prev, (parsed) => {
          const node = parsed[nodeId];
          if (!node) return;

          const parentId = node.parent_id;
          delete parsed[nodeId];

          if (parentId !== null && parsed[parentId]) {
            const stillHasChild = Object.values(parsed).some(
              (n) => n.parent_id === parentId
            );

            if (!stillHasChild) {
              parsed[parentId].type = "end";
              parsed[parentId].text = "";
            }
          }
        })
      );
    },
    [updateContentSafe]
  );

  const onRemoveBranch = useCallback(
    (nodeId: number) => {
      const parsed: ParsedNode = JSON.parse(content);
      const nodes = collectBranchNodes(nodeId, parsed).filter(
        (n) => n.id !== nodeId
      );

      setRemoveBranchPreview({
        nodeId,
        branchNodes: nodes,
      });
    },
    [content, collectBranchNodes]
  );

  const confirmRemoveBranch = useCallback(() => {
    if (!removeBranchPreview) return;

    const { nodeId } = removeBranchPreview;

    updateContentSafe((prev) => 
      updateContent(prev, (parsed) => {
        const removeDown = (id: number) => {
          Object.values(parsed).forEach((n) => {
            if (n.parent_id === id) {
              removeDown(n.id);
              delete parsed[n.id];
            }
          });
        };

        const node = parsed[nodeId];
        removeDown(nodeId);

        parsed[nodeId] = {
          ...node,
          type: "end",
          text: "",
        };
      })
    );

    setRemoveBranchPreview(null);
  }, [removeBranchPreview, updateContentSafe]);

  const cancelRemoveBranch = useCallback(() => {
    setRemoveBranchPreview(null);
  }, []);

  return {
    content,
    treeData,
    hideMap,
    removeBranchPreview,
    handlers: {
      onToggle,
      onSelect,
      onAddChild,
      onAddQuestion,
      onEdit,
      onRemoveChild,
      onRemoveBranch,
      confirmRemoveBranch,
      cancelRemoveBranch
    },
  };
};

export default memo(CardMeFormLeafNode);
